import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Unarchive as UnarchiveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { getArchivedHires, NewHire } from '../../services/hireService';
import { getOwnerArchivedHires } from '../../services/ownerArchiveService';
import { unarchiveService } from '../../services/unarchiveService';

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

interface ArchivedProps {
  onBack?: () => void;
  isOwner?: boolean;
  onUnarchiveComplete?: () => void;
}

const Archived: React.FC<ArchivedProps> = ({ onBack, isOwner = false, onUnarchiveComplete }) => {
  const [archivedHires, setArchivedHires] = useState<NewHire[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hireToUnarchive, setHireToUnarchive] = useState<NewHire | null>(null);
  const [unarchiving, setUnarchiving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const theme = useTheme();

  const fetchArchivedHires = async () => {
    try {
      setLoading(true);
      setError(null);
      const archived = isOwner ? await getOwnerArchivedHires() : await getArchivedHires();
      setArchivedHires(archived);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch archived hires');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedHires();
  }, [isOwner]);

  const handleUnarchiveClick = (hire: NewHire) => {
    setHireToUnarchive(hire);
    setConfirmOpen(true);
  };

  const handleUnarchiveCancel = () => {
    setConfirmOpen(false);
    setHireToUnarchive(null);
  };

  const handleUnarchiveConfirm = async () => {
    if (!hireToUnarchive) return;
    try {
      setUnarchiving(true);
      await unarchiveService.unarchiveHire(hireToUnarchive);
      setSnackbar({ open: true, message: `${hireToUnarchive.fullName} has been unarchived successfully`, severity: 'success' });
      setConfirmOpen(false);
      setHireToUnarchive(null);
      await fetchArchivedHires();
      onUnarchiveComplete?.();
    } catch (err) {
      console.error('Unarchive failed:', err);
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to unarchive hire', severity: 'error' });
    } finally {
      setUnarchiving(false);
    }
  };

  const filteredHires = archivedHires.filter((hire) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (hire.fullName && hire.fullName.toLowerCase().includes(q)) ||
      (hire.email && hire.email.toLowerCase().includes(q)) ||
      (hire.mobileNumber && hire.mobileNumber.toLowerCase().includes(q))
    );
  });

  const tableColumns = [
    'Unarchive',
    'Created At',
    'Updated At',
    'Full Name',
    'State',
    'Email',
    'Mobile Number',
    'Instagram',
    'Reference',
    'Recruiter Numbers',
    'Recruiter Emails',
  ];

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h6"
          color="error"
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.error.main}`,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(211, 47, 47, 0.1)'
              : 'rgba(211, 47, 47, 0.05)',
            textAlign: 'center'
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header row */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        flexWrap: 'wrap',
        gap: 2,
      }}>
        <Typography
          variant="h5"
          sx={{
            p: 2,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.12) 100%)'
              : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            borderRadius: 2,
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.3)'}`,
            fontWeight: 600,
            color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
          }}
        >
          Archived Hires ({filteredHires.length})
        </Typography>
        {onBack && (
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
              color: '#ffffff',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '12px',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(102, 126, 234, 0.4)'
                : '0 4px 20px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 50%, #764ba2 100%)',
                transform: 'translateY(-2px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 25px rgba(102, 126, 234, 0.5)'
                  : '0 8px 25px rgba(102, 126, 234, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Back to Active Hires
          </Button>
        )}
      </Box>

      {/* Search bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by name, email, or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearch('')} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 500 }}
        />
      </Box>

      {/* Scrollable table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          maxHeight: 600,
          overflow: 'auto',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(102, 126, 234, 0.15)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            },
          },
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
        }}
      >
        <Table stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {tableColumns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#e8eaf6',
                    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textAlign: 'center',
                    py: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    whiteSpace: 'nowrap',
                    minWidth: column === 'Unarchive' ? 100 : index <= 2 ? 120 : index === 3 ? 150 : 100,
                  }}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHires.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    {search ? 'No archived hires match your search' : 'No archived hires found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredHires.map((hire) => (
                <TableRow
                  key={hire.id}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.02)'
                        : 'rgba(102, 126, 234, 0.03)',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(118, 75, 162, 0.03)',
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(102, 126, 234, 0.08)',
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Tooltip title="Unarchive this hire">
                      <IconButton
                        onClick={() => handleUnarchiveClick(hire)}
                        sx={{
                          color: theme.palette.mode === 'dark' ? '#4caf50' : '#2e7d32',
                          '&:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          },
                        }}
                      >
                        <UnarchiveIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(hire.created_at)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(hire.update_timestamp)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>{hire.fullName}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{hire.state}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>{hire.email}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{hire.mobileNumber}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{hire.instagram}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{hire.reference}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{hire.recruiter_numbers}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{hire.recruiter_emails}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Unarchive Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleUnarchiveCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Confirm Unarchive</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to unarchive <strong>{hireToUnarchive?.fullName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will send an onboarding email to <strong>{hireToUnarchive?.email}</strong>, create a new active hire record, and remove from the archive.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnarchiveCancel} variant="outlined" disabled={unarchiving}>
            Cancel
          </Button>
          <Button
            onClick={handleUnarchiveConfirm}
            variant="contained"
            color="success"
            disabled={unarchiving}
            startIcon={unarchiving ? <CircularProgress size={18} color="inherit" /> : <UnarchiveIcon />}
          >
            {unarchiving ? 'Unarchiving...' : 'Unarchive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Archived;
